import { Typography, Button, Timeline, Divider, Tooltip } from "antd";
import {
  CloseOutlined,
  ShareAltOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import {
  FolderIcon,
  ArchiveBoxIcon,
  DocumentTextIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  CodeBracketIcon,
  CircleStackIcon,
  LockClosedIcon,
  StarIcon,
  HeartIcon,
  UserGroupIcon,
} from "@heroicons/react/24/solid";
import { VaultFolder } from "@/types";
import { formatFullDate, getRelativeTimeString } from "@/utils/formatters";
import { ACTIVITY_ACTIONS } from "@/utils/constants";
import { useAuditLog } from "@/hooks/useAuditLog";

const { Title, Text } = Typography;

const iconMap: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  folder: FolderIcon,
  "folder-open": ArchiveBoxIcon,
  file: DocumentTextIcon,
  picture: PhotoIcon,
  video: VideoCameraIcon,
  sound: MusicalNoteIcon,
  code: CodeBracketIcon,
  database: CircleStackIcon,
  lock: LockClosedIcon,
  star: StarIcon,
  heart: HeartIcon,
  team: UserGroupIcon,
};

interface FolderDetailPanelProps {
  folder: VaultFolder;
  onClose: () => void;
  onOpen: () => void;
  onShare: () => void;
  canShare?: boolean;
}

interface MetadataRowProps {
  label: React.ReactNode;
  value: React.ReactNode;
}

function MetadataRow({ label, value }: MetadataRowProps) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "10px 0",
        borderBottom: "1px solid #F3F4F6",
      }}
    >
      <div style={{ color: "#6B7280", fontSize: 13 }}>{label}</div>
      <Text style={{ fontSize: 13, color: "#1F2937" }}>{value}</Text>
    </div>
  );
}

export default function FolderDetailPanel({
  folder,
  onClose,
  onOpen,
  onShare,
  canShare = true,
}: FolderDetailPanelProps) {
  const { activities, isLoading: activitiesLoading } = useAuditLog(folder.id, 10);

  const IconComponent = iconMap[folder.icon] || FolderIcon;
  const isShared = folder.sharedWith?.length > 0;

  const getTimelineItems = () => {
    return activities.map((activity) => {
      const actionText = ACTIVITY_ACTIONS[activity.action] || activity.action;
      const hasEmail = activity.performedBy?.email;

      const nameElement = hasEmail ? (
        <Tooltip title={activity.performedBy.email} placement="top">
          <Text strong style={{ cursor: "pointer", borderBottom: "1px dashed #D1D5DB" }}>
            {activity.performedBy.name}
          </Text>
        </Tooltip>
      ) : (
        <Text strong>{activity.performedBy?.name || "Unknown"}</Text>
      );

      return {
        key: activity.id,
        children: (
          <>
            <Text style={{ fontSize: 12 }}>
              {nameElement} {actionText}
            </Text>
            <br />
            <Tooltip title={formatFullDate(activity.performedAt)} placement="top">
              <Text type="secondary" style={{ fontSize: 11, cursor: "default" }}>
                {getRelativeTimeString(activity.performedAt)}
              </Text>
            </Tooltip>
          </>
        ),
      };
    });
  };

  return (
    <div
      className="detail-panel"
      style={{
        width: 340,
        padding: 24,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: "#ffffff",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <Title level={5} style={{ margin: 0, fontSize: 16 }}>
          Folder details
        </Title>
        <Button
          type="text"
          icon={<CloseOutlined style={{ fontSize: 14 }} />}
          onClick={onClose}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      </div>

      {/* Preview area */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "28px 20px",
          background: "#F9FAFB",
          borderRadius: 12,
          marginBottom: 24,
          border: "1px solid #E5E7EB",
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 12,
            background: `${folder.color || "#0F6E56"}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 14,
          }}
        >
          <IconComponent
            style={{
              width: 32,
              height: 32,
              color: folder.color || "#0F6E56",
            }}
          />
        </div>
        <Text
          strong
          style={{
            fontSize: 14,
            textAlign: "center",
            wordBreak: "break-word",
            lineHeight: 1.4,
            maxWidth: "100%",
            color: "#1F2937",
          }}
        >
          {folder.name}
        </Text>
        {isShared && (
          <Text
            type="secondary"
            style={{ fontSize: 12, marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}
          >
            <ShareAltOutlined style={{ fontSize: 11 }} />
            Shared with {folder.sharedWith.length} {folder.sharedWith.length === 1 ? "person" : "people"}
          </Text>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24 }}>
        <Button
          type="primary"
          icon={<FolderOpenOutlined />}
          onClick={onOpen}
          style={{ flex: 1, height: 44, borderRadius: 10, fontWeight: 500 }}
        >
          Open
        </Button>
        {canShare && (
          <Button
            icon={<ShareAltOutlined />}
            onClick={onShare}
            style={{ flex: 1, height: 44, borderRadius: 10, fontWeight: 500 }}
          >
            Share
          </Button>
        )}
      </div>

      {/* Metadata */}
      <div style={{ marginBottom: 20 }}>
        <MetadataRow label="Items" value={`${folder.itemCount ?? 0} ${folder.itemCount === 1 ? "item" : "items"}`} />
        {folder.createdAt && (
          <MetadataRow label="Created" value={formatFullDate(folder.createdAt)} />
        )}
        {folder.createdBy && (
          <MetadataRow
            label="Created by"
            value={
              <Tooltip title={folder.createdBy.email} placement="top">
                <span style={{ cursor: "pointer", borderBottom: "1px dashed #D1D5DB" }}>
                  {folder.createdBy.name}
                </span>
              </Tooltip>
            }
          />
        )}
        <MetadataRow label="Location" value={folder.parentFolderId ? "Subfolder" : "Root"} />
      </div>

      <Divider style={{ margin: "8px 0 16px 0" }} />

      {/* Activity */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <Text
          style={{
            color: "#6B7280",
            fontSize: 13,
            display: "block",
            paddingBottom: 14,
            background: "#ffffff",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          Recent activity
        </Text>
        <div style={{ flex: 1, overflow: "auto" }}>
          {activitiesLoading ? (
            <Text type="secondary" style={{ fontSize: 13 }}>
              Loading...
            </Text>
          ) : activities.length > 0 ? (
            <Timeline items={getTimelineItems()} />
          ) : (
            <Text type="secondary" style={{ fontSize: 13 }}>
              No recent activity
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}
